import { noop } from 'lodash';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';

import { AnnotatedPDFViewerContext } from './AnnotatedPDFViewer.context';

function useAnnotatedPDFViewerContextValue({
  documentId,
  overrideAnnotations,
  overrideActiveAnnotationIndex,
  overrideSearchQuery,
  onUpdateSearchQuery,
  overrideSearchLoading,
  showCitations = false,
  showCitationCoachmark = true,
  showCopyCitationsButton = false,
}: AnnotatedPDFViewerProviderProps): AnnotatedPDFViewerContext {
  const [annotations, setAnnotations] = useState<
    DocumentSearchResult[] | undefined
  >(overrideAnnotations);
  // Store the most recently searched paragraphIds so when the user clicks on the citation tooltip,
  // we know whether they've already fetched them and we just want to cycle through the active annotations
  const lastCitationParagraphIdsRef = useRef<string[]>([]);
  const [activeAnnotationIndex, setActiveAnnotationIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [currentCitationRequestId, setCurrentCitationRequestId] =
    useState<number>(0);
  const [overrideDocumentId, setOverrideDocumentId] = useState<string | null>(
    null
  );

  const moveActiveAnnotation = (direction: number) => {
    if (!annotations || annotations.length === 0) return;

    const nextIndex =
      (activeAnnotationIndex + direction + annotations.length) %
      annotations.length;
    setActiveAnnotationIndex(nextIndex);
    const nextDocumentId = annotations[nextIndex]?.documentID;
    if (nextDocumentId !== overrideDocumentId) {
      setOverrideDocumentId(nextDocumentId ?? null);
    }
  };

  const incrementCitationRequestId = () => {
    const newId = currentCitationRequestId + 1;
    setCurrentCitationRequestId(newId);
    return newId;
  };

  // Sync overrideAnnotations to annotations when it changes
  useEffect(() => {
    setAnnotations(overrideAnnotations);
  }, [overrideAnnotations]);

  // Sync overrideActiveAnnotationIndex to activeAnnotationIndex when it changes
  // This effect handles all cases: when overrideActiveAnnotationIndex is provided as a number,
  // when it's undefined (reset to 0), and when annotations change without an explicit index
  useEffect(() => {
    if (overrideActiveAnnotationIndex === undefined) {
      // If overrideActiveAnnotationIndex is undefined, reset to 0
      // This handles both the initial case and when it changes from a number to undefined
      setActiveAnnotationIndex(0);
    } else {
      // Always update, even if the value is the same, to ensure downstream components re-render
      setActiveAnnotationIndex(overrideActiveAnnotationIndex);
    }
  }, [overrideActiveAnnotationIndex, overrideAnnotations]);

  // Sync overrideSearchQuery to searchQuery when it changes
  useEffect(() => {
    // Use explicit type check to ensure we don't set undefined value
    if (typeof overrideSearchQuery === 'string') {
      setSearchQuery(overrideSearchQuery);
    }
  }, [overrideSearchQuery]);

  // Sync overrideSearchLoading to searchLoading when it changes
  useEffect(() => {
    if (typeof overrideSearchLoading === 'boolean') {
      setSearchLoading(overrideSearchLoading);
    }
  }, [overrideSearchLoading]);

  return {
    documentId: overrideDocumentId ?? documentId ?? undefined,
    setOverrideDocumentId,
    annotations,
    setAnnotations,
    activeAnnotationIndex,
    setActiveAnnotationIndex,
    searchQuery,
    setSearchQuery,
    moveActiveAnnotation,
    overrideSearchQuery,
    onUpdateSearchQuery: onUpdateSearchQuery ?? noop,
    searchLoading,
    setSearchLoading,
    currentCitationRequestId,
    incrementCitationRequestId,
    showCitations,
    showCitationCoachmark,
    showCopyCitationsButton,
    lastCitationParagraphIdsRef,
  };
}

export interface AnnotatedPDFViewerProviderProps {
  documentId: string | null;
  overrideAnnotations?: DocumentSearchResult[];
  overrideActiveAnnotationIndex?: number;
  overrideSearchQuery?: string;
  onUpdateSearchQuery?: () => void;
  overrideSearchLoading?: boolean;
  // showCitations exposes citations in the summary
  showCitations?: boolean;
  // showCitationCoachmark controls the one-time "click icon to cycle through
  // evidence" coachmark on citation icons; disable it in hosts where the
  // coachmark's instructions don't apply (e.g. the chat side content pane)
  showCitationCoachmark?: boolean;
  // showCopyCitationsButton exposes a "copy with citations" button on the
  // document search bar
  showCopyCitationsButton?: boolean;
}

export const AnnotatedPDFViewerProvider = ({
  children,
  ...props
}: PropsWithChildren<AnnotatedPDFViewerProviderProps>) => {
  const value = useAnnotatedPDFViewerContextValue(props);

  return (
    <AnnotatedPDFViewerContext.Provider value={value}>
      {children}
    </AnnotatedPDFViewerContext.Provider>
  );
};
